/**
 * The admin partner-detail Links table's query, as the list endpoint takes it.
 *
 * EVERY narrowing is the server's (MKT-834's `tag_ids` / `untagged`, MKT-877's
 * `status` / `ordering`, and the pre-existing `search` / `batch_id`): the table
 * loads ONE page, so a predicate applied in the browser could only ever narrow
 * that page, and a count or facet derived from it could only ever describe it.
 * MKT-878 retired the client-side status filter, status counts and batch facet
 * this module used to own — the pills now read the response's `status_counts`
 * and sorting re-queries the server.
 *
 * What remains here is the mapping from a chosen view onto the request, kept
 * framework-free so it stays unit-testable at the boundary that matters: the
 * params on the wire.
 */
import type { ListPartnerCouponsParams } from '@/api/gen'
import { COUPON_STATUSES, type CouponStatus } from '@/domain/coupon-status'
import type { AffiliateAdminCouponSchema, AffiliateCouponTagSchema } from '@/domain/coupons'

/** The status pills' values: every lifecycle status, plus the "all" sentinel. */
export type CouponStatusFilter = 'all' | CouponStatus

export const isCouponStatus = (value: string): value is CouponStatus =>
  COUPON_STATUSES.some((status) => status === value)

/**
 * Which tag bucket the table is scoped to. "No tags at all" is a bucket of its
 * own rather than a reserved id because the backend takes it as a separate
 * `untagged` flag — a sentinel inside `tag_ids` could collide with a real tag.
 */
export type CouponTagFilter =
  | { kind: 'any' }
  | { kind: 'tag'; tagId: string }
  | { kind: 'untagged' }

export const ANY_COUPON_TAG_FILTER: CouponTagFilter = { kind: 'any' }

/**
 * The untagged bucket's single-string form, shared by the facet `<SelectField>`
 * and the `?tag=` deep link. Tag ids are uuids, so this literal can never be
 * mistaken for one.
 */
export const UNTAGGED_TAG_VALUE = 'untagged'

/** The bucket as one string: '' for "any tag", so it doubles as the select's
 *  all-sentinel and as an omitted `?tag=`. */
export const couponTagFilterValue = (filter: CouponTagFilter): string => {
  switch (filter.kind) {
    case 'any':
      return ''
    case 'untagged':
      return UNTAGGED_TAG_VALUE
    case 'tag':
      return filter.tagId
  }
}

export const parseCouponTagFilter = (value: string): CouponTagFilter => {
  if (value === '') {
    return ANY_COUPON_TAG_FILTER
  }
  if (value === UNTAGGED_TAG_VALUE) {
    return { kind: 'untagged' }
  }

  return { kind: 'tag', tagId: value }
}

/** The list endpoint's tag params for a bucket. Both keys are absent for "any
 *  tag", so an unfiltered read keeps the exact request it had before tag
 *  filtering existed. */
export type CouponTagQueryParams = {
  tag_ids?: string[]
  untagged?: boolean
}

export const couponTagQueryParams = (filter: CouponTagFilter): CouponTagQueryParams => {
  switch (filter.kind) {
    case 'any':
      return {}
    case 'untagged':
      return { untagged: true }
    case 'tag':
      return { tag_ids: [filter.tagId] }
  }
}

/**
 * The columns the endpoint can order by (`AffiliateCouponOrderingEnum`).
 * Deliberately NOT status: MKT-877 scoped server-side status ordering out, and a
 * column that sorts only the loaded page would rank the 10 rows this load
 * happened to fetch. `expires_at` is derived server-side from
 * `created_at + days_valid`, with never-expiring codes last ascending.
 */
export const COUPON_SORT_FIELDS = ['code', 'created_at', 'discount_value', 'expires_at'] as const

export type CouponSortField = (typeof COUPON_SORT_FIELDS)[number]

export const isCouponSortField = (value: string): value is CouponSortField =>
  COUPON_SORT_FIELDS.some((field) => field === value)

export const COUPON_SORT_DIRS = ['asc', 'desc'] as const

export type CouponSortDir = (typeof COUPON_SORT_DIRS)[number]

export type CouponSort = { field: CouponSortField; dir: CouponSortDir }

/** The endpoint's own default, which the grid shows as the resting sort so the
 *  header indicator describes the order the rows are actually in. */
export const DEFAULT_COUPON_SORT: CouponSort = { field: 'created_at', dir: 'desc' }

/** `-` prefix = descending, per `AffiliateCouponOrderingEnum`. */
export const couponOrdering = ({ field, dir }: CouponSort): string =>
  dir === 'desc' ? `-${field}` : field

/**
 * The page sizes offered, which MUST stay the set `DataTable` passes to
 * `pageSizeOptions` — the grid renders no option for a size it was not offered,
 * so a URL naming one outside this set would strand the pager on a blank value.
 */
export const COUPON_PAGE_SIZES = [10, 25, 50] as const

export const DEFAULT_COUPON_PAGE_SIZE = 10

export const isCouponPageSize = (value: number): boolean =>
  COUPON_PAGE_SIZES.some((size) => size === value)

/** One view of the list, already validated — the shape the URL parses into and
 *  the request is built from. `page` is 1-based, like the endpoint. */
export type CouponListFilters = {
  page: number
  pageSize: number
  /** Case-insensitive substring match on the code; '' = no search. */
  search: string
  status: CouponStatusFilter
  /** '' = every batch. */
  batchId: string
  tagFilter: CouponTagFilter
  sort: CouponSort
}

export const DEFAULT_COUPON_LIST_FILTERS: CouponListFilters = {
  page: 1,
  pageSize: DEFAULT_COUPON_PAGE_SIZE,
  search: '',
  status: 'all',
  batchId: '',
  tagFilter: ANY_COUPON_TAG_FILTER,
  sort: DEFAULT_COUPON_SORT,
}

/**
 * The params MKT-834 and MKT-877 added, which the checked-in orval client
 * predates — `ListPartnerCouponsParams` still carries only search / is_active /
 * prefix / batch_id / page / page_size. Declared as a narrow intersection rather
 * than regenerated: a full regen of this app's client has previously pulled in
 * unrelated breaking drift, so widening the one param type is the smaller risk.
 * Retire it with the regen the TODO in `src/domain/coupons.ts` tracks.
 */
export type CouponListQueryParams = ListPartnerCouponsParams &
  CouponTagQueryParams & {
    status?: CouponStatus
    ordering?: string
  }

/**
 * A view as the endpoint's query params. Only keys that carry a value are
 * emitted: the params object IS the query key, so an `undefined` property would
 * make an otherwise identical view a cache miss.
 */
export const couponListParams = (filters: CouponListFilters): CouponListQueryParams => {
  const search = filters.search.trim()
  const params: CouponListQueryParams = {
    page: filters.page,
    page_size: filters.pageSize,
    ...couponTagQueryParams(filters.tagFilter),
  }

  if (search !== '') {
    params.search = search
  }
  if (filters.status !== 'all') {
    params.status = filters.status
  }
  if (filters.batchId !== '') {
    params.batch_id = filters.batchId
  }
  // Omitted at the endpoint's own default, so the resting view's request — and
  // its cache entry — stays the one a partner's first visit produces.
  if (
    filters.sort.field !== DEFAULT_COUPON_SORT.field ||
    filters.sort.dir !== DEFAULT_COUPON_SORT.dir
  ) {
    params.ordering = couponOrdering(filters.sort)
  }

  return params
}

/** Whether anything is narrowing the list. Distinguishes "this partner has no
 *  links" from "no links match", which the empty state must not conflate: a
 *  filtered miss has to keep the filter bar mounted so the admin can clear it. */
export const hasCouponListNarrowing = (filters: CouponListFilters): boolean =>
  filters.search.trim() !== '' ||
  filters.status !== 'all' ||
  filters.batchId !== '' ||
  filters.tagFilter.kind !== 'any'

// Batch codes are minted as `<PREFIX>-<random>` when the create form supplied a
// prefix and as a bare `<random>` when it did not, so the segment before the
// first hyphen recovers the prefix whenever there is one.
const PREFIX_DELIMITER = '-'

export const extractCouponPrefix = (code: string): string | null => {
  const index = code.indexOf(PREFIX_DELIMITER)

  return index > 0 ? code.slice(0, index) : null
}

/**
 * A name for the batch the table is currently scoped to, read off the loaded
 * page: with `batch_id` on the request every row belongs to that batch, so its
 * shared prefix names it. `null` both when the page carries no rows (a page past
 * the end, or a load still in flight) and when the batch was minted WITHOUT a
 * prefix — in either case the caller names the scope generically rather than
 * printing a raw uuid. Deliberately not falling back to the first row's code: the
 * chip reads as the batch's identity, and a per-page code would rename it on
 * every page of the same batch.
 */
export const batchScopeLabel = (
  coupons: readonly Pick<AffiliateAdminCouponSchema, 'code'>[],
): string | null => {
  const [first] = coupons

  return first === undefined ? null : extractCouponPrefix(first.code)
}

/**
 * The distinct tags a set of coupons carries, by name. NOT a facet source — the
 * facet is the partner's whole tag list, which the server filters against. This
 * answers the narrower "which tags could a bulk removal act on".
 */
export const distinctCouponTags = (
  coupons: readonly Pick<AffiliateAdminCouponSchema, 'tags'>[],
): AffiliateCouponTagSchema[] => {
  const tags = new Map<string, AffiliateCouponTagSchema>()

  for (const coupon of coupons) {
    for (const tag of coupon.tags) {
      tags.set(tag.id, tag)
    }
  }

  return [...tags.values()].sort((left, right) => left.name.localeCompare(right.name))
}
